/**
 * Work orders — projects and extras, which are one object at two scales.
 *
 * `SERVICES.md` §Billing models: "Projects and extras are one object at two scales —
 * a *work order*. A project carries a quote and milestones and is invoiced on its
 * own; an extra is priced off the rate card and accrues to the monthly invoice." So
 * there is one table, one lifecycle and two reference kinds, and `kind` is what says
 * which — `WO-YYYY-NNN` for a project, `REQ-NNN` for an extra.
 *
 * Everything here is the **wire** shape: snake_case, refs rather than ids, money as
 * `{ amount, currency }` in minor units. `apps/api` validates with these objects and
 * session 19's forms resolve with the same ones (`.docs/00-index.md` §5).
 *
 * The one thing that is deliberately not here is the price. A work order's amounts
 * come from `catalog.computePrice` and are snapshotted onto `work_order_line` and
 * `pricing_breakdown`; nothing in this file computes, and `packages/shared/src/
 * pricing.ts` is where the arithmetic lives.
 */
import { z } from 'zod';
import { isRef } from '../refs.js';
import { workOrderMachine } from '../states.js';
import { CatalogCodeSchema, ComputedPriceSchema } from './catalog.js';
import { DateOnlySchema, MoneySchema, Rfc3339Schema } from './primitives.js';
import { ClientSlugSchema } from './refs.js';
/* ---------------------------------------------------------------- the words */
/**
 * `SERVICES.md` §Billing models. A project is quoted with milestones and invoiced on
 * its own; an extra is priced off the rate card and lands on the next monthly
 * invoice.
 */
export const WORK_ORDER_KINDS = ['project', 'extra'];
export const WorkOrderKindSchema = z
    .enum(WORK_ORDER_KINDS)
    .describe('project — a quoted piece of work with milestones and its own invoices, referenced ' +
    'WO-YYYY-NNN. extra — work outside the client’s package, priced off the rate card and ' +
    'billed in arrears on the next monthly invoice, referenced REQ-NNN.');
/**
 * The three buttons under *Timing* on `mockups/client/requests.html`.
 *
 * "Rush work is not a surcharge — it just changes what we can promise", which is why
 * this is on the request and never on the price.
 */
export const WORK_ORDER_TIMINGS = ['no_rush', 'this_month', 'this_week'];
export const WorkOrderTimingSchema = z
    .enum(WORK_ORDER_TIMINGS)
    .describe('How soon it is wanted. Rush is not a surcharge — it changes what the studio can ' +
    'promise, not what it costs.');
/**
 * What one line of a quote is.
 *
 * `template` and `add_on` are a project's build-up — the five rows in the quote fold
 * on `mockups/client/projects.html`. `unit` is an extra's: a rate-card unit at the
 * client's own tier, already rounded, which is what an invoice line is copied from.
 * `discount` is for a line that comes **off** a total; session 11 writes none (the
 * retainer discount, the tier and the rounding are steps in `pricing_breakdown`, not
 * lines), and 12 and 14 use it.
 */
export const WORK_ORDER_LINE_KINDS = ['template', 'add_on', 'unit', 'discount'];
export const WorkOrderLineKindSchema = z
    .enum(WORK_ORDER_LINE_KINDS)
    .describe('template — the project template the quote starts from. add_on — a line on top of it. ' +
    'unit — a rate-card unit at this client’s tier. discount — an amount that comes off.');
/**
 * `ARCHITECTURE.md` §Impersonation and audit: "a staff member who needs to approve on
 * a client's behalf records it as a staff action with the client's written consent
 * attached." That is what `offline` means, and the consent file is required for it.
 *
 * `rolling` is session 12's third route and it is still the client's own approval,
 * given once instead of once a month: `retainer_item.overage_policy = 'rolling'` is a
 * **standing** approval that an owner or an admin set with their name against it
 * (`studio/client-detail.html` prints "rolling approval" in the Approved column), and
 * `retainers.recordDeliverable` is the only caller — it creates the extra the overage
 * needs and stamps `rolling_set_by` as the approver. There is no consent file, because
 * the consent is a row somebody wrote in the portal.
 */
export const APPROVAL_VIAS = ['portal', 'offline', 'rolling'];
export const ApprovalViaSchema = z
    .enum(APPROVAL_VIAS)
    .describe('portal — an owner or admin approved it themselves, which is the contract event. ' +
    'offline — staff recorded an approval given elsewhere, with the written consent ' +
    'attached as a file. rolling — the standing approval on a retainer item, given ' +
    'once by an owner or admin and applied to each period’s overage as it happens.');
/**
 * `.docs/00-index.md` §6, recurring extras: "An approved extra may be `recurrence:
 * 'monthly'`; the billing run bills it every period until cancelled." `REQ-114`, the
 * Pinterest channel, is the one in the mockups.
 */
export const WORK_ORDER_RECURRENCES = ['monthly'];
export const WorkOrderRecurrenceSchema = z
    .enum(WORK_ORDER_RECURRENCES)
    .describe('monthly — an extra that is billed again every period until it is cancelled, like an ' +
    'added social channel. Left out, it is billed once.');
/**
 * Which form the request came from. `.docs/11-work-orders.md` §DRY watchlist: "The
 * composer endpoint is one; the catalogue-driven request and the free-text request
 * differ only in `source`."
 */
export const REQUEST_SOURCES = ['composer', 'catalogue'];
export const RequestSourceSchema = z
    .enum(REQUEST_SOURCES)
    .describe('composer — the free-text request form on the requests page. catalogue — the ticked ' +
    'items on the billing page’s rate card, which come with codes and a preferred start.');
/* ----------------------------------------------------------------- the ref */
/**
 * A work order reference, of **either** kind.
 *
 * `WorkOrderRefSchema` in `schemas/refs.ts` is `WO-` only and `RequestRefSchema` is
 * `REQ-` only; both stay, because a tool that only ever takes a project should say
 * so. This is the one for the tools that take either, which is most of them — the
 * lifecycle is the same object's.
 *
 * A `refine` rather than a regex: `.docs/00-index.md` §5 — "no regex on a ref
 * elsewhere" — and `isRef` is `REF_TABLE`'s own answer. It is enforced at parse and
 * invisible to JSON Schema, which is the safe direction (session 05's report).
 */
export const AnyWorkOrderRefSchema = z
    .string()
    .refine((value) => isRef('work_order', value) || isRef('request', value), 'Expected a work order reference like WO-2026-008, or a request reference like REQ-119.')
    .describe('A work order by reference: WO-YYYY-NNN for a project, REQ-NNN for an extra. ' +
    'Case-sensitive, exactly as it is shown.');
/* --------------------------------------------------------------- the pieces */
export const WorkOrderLineSchema = z
    .object({
    seq: z.int().describe('Where it sits in the quote, counting from 1. Display order.'),
    kind: WorkOrderLineKindSchema,
    code: z.string().describe('The catalog code it was priced from — "website_m", "reel", "hour".'),
    description: z.string().describe('What it reads as on the quote.'),
    qty: z.number().describe('How many. May be fractional where the unit is — 6.5 hours.'),
    unit_amount: MoneySchema.describe('What one of them costs.'),
    amount: MoneySchema.describe('qty × unit_amount, rounded to the minor unit.'),
})
    .describe('One line of a quote. A project’s lines are the USD Tier 1 list build-up the quote ' +
    'fold prints; an extra’s are in the client’s own currency and are what an invoice ' +
    'line is copied from.');
export const MilestoneSchema = z
    .object({
    seq: z.int().describe('Which milestone, counting from 1. They are delivered in order.'),
    title: z.string().describe('What it is called — "Design approval".'),
    percent: z.int().describe('Its share of the fixed total, as a whole percentage.'),
    amount: MoneySchema.describe('Its share of the total. The percentages sum to the total.'),
    due_on: DateOnlySchema.nullable().describe('When it is due, YYYY-MM-DD, or null.'),
    delivered_at: Rfc3339Schema.nullable().describe('When it was delivered, or null.'),
    invoice_id: z
        .string()
        .nullable()
        .describe('The invoice issued for it, or null while none has been.'),
    includes_scope_changes: z
        .boolean()
        .describe('True for the final milestone, whose invoice carries the scope changes approved ' +
        'during the project (SERVICES.md, Projects).'),
})
    .describe('One milestone of a project: a share of the total, invoiced when it is delivered.');
export const WorkOrderCommentSchema = z
    .object({
    id: z.string().describe('The comment’s id.'),
    actor_kind: z
        .enum(['user', 'agent', 'none'])
        .describe('Who wrote it: a person, an agent, or the system.'),
    actor_name: z.string().nullable().describe('Their name, for the thread, or null.'),
    staff: z.boolean().describe('True when it was written by the studio rather than the client.'),
    body: z.string().describe('What they said.'),
    at: Rfc3339Schema.describe('When they said it.'),
})
    .describe('One comment on a work order. Portal-only — it is not email.');
export const WorkOrderAttachmentSchema = z
    .object({
    file_id: z.string().describe('The stored file, by id. Download it at /api/files/:id/download.'),
    name: z.string().describe('The name it was uploaded under.'),
    mime: z.string().describe('Its media type.'),
    size: z.int().describe('Its length in bytes.'),
    at: Rfc3339Schema.describe('When it was attached.'),
})
    .describe('A brief, some footage or a reference, attached to the request it belongs to.');
/* --------------------------------------------------------- the work order */
export const WorkOrderSchema = z
    .object({
    ref: AnyWorkOrderRefSchema,
    client: ClientSlugSchema,
    kind: WorkOrderKindSchema,
    parent_ref: z
        .string()
        .nullable()
        .describe('The project this is a scope change on, or null. A scope change is an extra with a ' +
        'parent, and it lands on that project’s final milestone invoice.'),
    service: CatalogCodeSchema.describe('The service this is work on, by rate-card code.'),
    template: z
        .string()
        .nullable()
        .describe('The project template it was quoted from, by code, or null.'),
    title: z.string().describe('One line, as the client reads it in the ledger.'),
    scope: z.string().describe('What was asked for, in words. The client wrote the first draft.'),
    currency: z.string().describe('The client’s billing currency. Every amount here is in it.'),
    total_amount: MoneySchema.nullable().describe('This row’s **own** agreed total, excluding any scope change attached to it — a ' +
        'project’s milestones add up to exactly this figure, and a scope change is its own ' +
        'row with its own total. Null while it is still to be quoted — "We’ll quote". Null ' +
        'is never zero.'),
    quote_valid_until: DateOnlySchema.nullable().describe('The last day it can be approved. Thirty days from the proposal (.docs/00-index.md §6).'),
    quote_due_on: DateOnlySchema.nullable().describe('When the studio owes a price, for a request the rate card has no unit for. Two ' +
        'business days from the draft.'),
    recurrence: WorkOrderRecurrenceSchema.nullable().describe('monthly when it is billed again every period, or null for a one-off.'),
    deposit_on_approval: z
        .boolean()
        .describe('True when approving it issues the first milestone as a deposit invoice. False when ' +
        'the first milestone invoices on delivery like the rest.'),
    status: z
        .enum(workOrderMachine.states)
        .describe('draft (being written or waiting on a price), proposed (waiting on the client), ' +
        'approved, in_progress, delivered, billed. declined and cancelled are terminal.'),
    timing: WorkOrderTimingSchema,
    source: RequestSourceSchema,
    catalogue_codes: z
        .array(z.string())
        .describe('The rate-card codes the client ticked, for a request raised from the catalogue. ' +
        'Empty for a free-text one.'),
    preferred_start_on: DateOnlySchema.nullable().describe('The billing day the client asked a package change to start on, or null for ' +
        '"whenever you can".'),
    requested_by_kind: z
        .enum(['user', 'agent', 'none'])
        .describe('Who raised it: a person, an agent, or the system.'),
    proposed_at: Rfc3339Schema.nullable().describe('When the priced quote went to the client.'),
    approved_by_name: z
        .string()
        .nullable()
        .describe('Who approved it, by name. This record is the authority for the charge.'),
    approved_at: Rfc3339Schema.nullable().describe('When they approved it.'),
    approval_via: ApprovalViaSchema.nullable().describe('How the approval was given, or null while there is none.'),
    declined_at: Rfc3339Schema.nullable(),
    decline_note: z
        .string()
        .nullable()
        .describe('Why it stopped, in words. Whose words depends on which way it stopped, and the two ' +
        'cannot collide: `declined` is the client refusing it and this is their sentence; ' +
        '`cancelled` is the studio stopping it and this is the reason work_orders_cancel was ' +
        'given — the cancel dialog promises the client may read it, and the requests ledger ' +
        'is where they do. workOrderMachine makes both states terminal and neither reaches ' +
        'the other, so one row never carries both. Null when it stopped for neither reason, ' +
        'or when nobody said why.'),
    started_at: Rfc3339Schema.nullable(),
    delivered_at: Rfc3339Schema.nullable(),
    billed_at: Rfc3339Schema.nullable(),
    cancelled_at: Rfc3339Schema.nullable(),
    not_billed: z
        .boolean()
        .describe('True when the studio waived the charge. The work still counts as delivered and ' +
        'still reaches the invoice, at zero (REQ-116 in the mockups).'),
    waived_reason: z.string().nullable().describe('Why it was waived, or null.'),
    expired: z
        .boolean()
        .describe('True when it is proposed and its quote has run out. It cannot be approved; the ' +
        'studio re-proposes it.'),
    chased_at: Rfc3339Schema.nullable().describe('When the studio was last told the quote had gone unanswered. Once per quote.'),
    created_at: Rfc3339Schema,
    updated_at: Rfc3339Schema,
})
    .describe('A work order: a project quote or an extra, and everything about its state. Nothing ' +
    'money-related happens before approved_at is set.');
/**
 * Whether a row is a scope change on `parentRef` that will reach an invoice — the one
 * predicate behind every "project to date" figure in the portal.
 *
 * A scope change is "an extra with a `parent_ref`", and it bills on its project's
 * final milestone (`SERVICES.md` §Projects). Four states are not that:
 *
 *   - **not approved yet** — a proposal is not money anybody owes;
 *   - **declined** and **cancelled** — things that did not happen;
 *   - **waived** (`not_billed`) — `work_orders.waive` leaves the extra `delivered` so
 *     the value ledger still counts the work, and session 14 copies it on to the
 *     invoice **at zero**. Counting its `total_amount` would put money in a hero
 *     nobody will ever be asked for.
 *
 * Here rather than in either reader because there were two, and they disagreed:
 * `pages/client/projects/facts.ts` `scopeChangesOf` applied all four tests and
 * `readmodels/studio-client-detail.ts` `withScopeChangesFolded` applied **none** —
 * it folded every child with a `parent_ref`, so a declined or waived scope change
 * inflated the studio's project total and not the client's. Session 24 (R2-23) found
 * the same project reading $4,730.00, $5,050.00 and a hero that summed to $5,050.00
 * on three screens; this is the half of that which was a real disagreement rather
 * than a missing sentence. `pastDueBucketOf` above is the precedent for a predicate
 * living beside the schema it reads.
 */
export function isBillableScopeChange(row, parentRef) {
    return (row.parent_ref === parentRef &&
        row.approved_at !== null &&
        row.declined_at === null &&
        row.cancelled_at === null &&
        !row.not_billed);
}
export const WorkOrderDetailSchema = z
    .object({
    work_order: WorkOrderSchema,
    lines: z.array(WorkOrderLineSchema).describe('The quote, line by line, in display order.'),
    milestones: z.array(MilestoneSchema).describe('The schedule, in order. Empty for an extra.'),
    comments: z.array(WorkOrderCommentSchema).describe('The thread, oldest first.'),
    attachments: z.array(WorkOrderAttachmentSchema).describe('What was attached to the request.'),
    scope_changes: z
        .array(WorkOrderSchema)
        .describe('Extras raised against this project, each with its own approval.'),
    pricing: z
        .array(ComputedPriceSchema)
        .describe('The price build-up as it stood when the quote was made: the list, the bundle or ' +
        'retainer discount, the market tier, the conversion and the rounding. One entry ' +
        'for a project, one per unit for an extra, and empty while nothing has been priced.'),
})
    .describe('One work order with everything its page shows.');
/* ------------------------------------------------------------ the decisions */
/**
 * One row of "what is waiting on you" (`mockups/client/requests.html` §1).
 *
 * `kind` already carries the four decision kinds the page shows, though session 11
 * only ever produces two of them: session 12 adds `package_change` and
 * `content_plan`, and session 17's decisions read model merges the three sources
 * into one list. A shape that had to be widened later would be a shape 17 could not
 * merge without changing this file.
 */
export const DECISION_KINDS = ['quote', 'extra', 'package_change', 'content_plan'];
/**
 * What each kind is called inside a sentence.
 *
 * Here rather than in either portal because **both ends of one button need it**: the
 * studio's Nudge dialog says "everybody on this account gets one email about the
 * {noun}", and the mail it then sends says "That {noun} is still open". Those two came
 * from separate tables — one in `pages/studio/clientDetail/NudgeButton.tsx`, one each
 * in `approval-reminder.ts` and `approval-requested.ts` — so the promise and the mail
 * could disagree with nothing to notice.
 *
 * A `Record` over `DecisionKind`, so a fifth kind is a compile error in all three.
 */
export const DECISION_KIND_WORDS = {
    quote: 'quote',
    extra: 'extra',
    package_change: 'package change',
    content_plan: 'content plan',
};
export const OpenDecisionSchema = z
    .object({
    kind: z
        .enum(DECISION_KINDS)
        .describe('quote — a project quote. extra — work past an allowance. package_change and ' +
        'content_plan are the retainer’s two, added in session 12.'),
    ref: z.string().describe('What is waiting, by reference.'),
    title: z.string().describe('What it is for.'),
    service: z.string().nullable().describe('The service it is work on, by code, or null.'),
    amount: MoneySchema.nullable().describe('What approving it commits. Null for something that costs nothing, or is still to ' +
        'be quoted.'),
    recurrence: WorkOrderRecurrenceSchema.nullable().describe('monthly when approving it commits to that amount every period.'),
    valid_until: DateOnlySchema.nullable().describe('The last day it can be approved.'),
    proposed_at: Rfc3339Schema.describe('When it was sent.'),
    approvers_only: z
        .boolean()
        .describe('True when only an owner or an admin may answer it. That is every decision this ' +
        'system has today: the three that cost money go through the money-approval rule, ' +
        'and a content-plan sign-off goes through its own (00-index.md §6: "Owner/admin ' +
        'sign off"). It is a field rather than a constant so a decision anybody on the ' +
        'account could give would say so.'),
})
    .describe('One decision waiting on the client. Nothing about it has been charged.');
/* ------------------------------------------------------------- the requests */
/** The unit an extra is priced from: a rate-card unit, or a service’s fixed add-on. */
export const PricedUnitSchema = z
    .strictObject({
    service: CatalogCodeSchema.describe('The service, by code — "social_content_base".'),
    unit: CatalogCodeSchema.describe('Its unit or one of its fixed add-ons, by code — "reel", "hour", "website_extra_page". ' +
        'catalog_list_services and catalog_list_project_templates list them.'),
    qty: z
        .number()
        .gt(0)
        .max(100_000)
        /*
         * Three decimal places, because `work_order_line.qty` is `numeric(12,3)` and a
         * quantity finer than the column would be truncated on the way in — while
         * `amount` was computed from the number the caller sent. The CHECK
         * `amount = round(qty * unit_amount)` then fails, which is a 500 for what is
         * plainly a bad request.
         */
        .refine((value) => Number(value.toFixed(3)) === value, 'A quantity has at most three decimal places.')
        .describe('How many. More than nothing, and at most three decimal places — the unit may ' +
        'be fractional (6.5 Odoo hours) but a line of no units is not a line.'),
})
    .describe('One priced line of an extra.');
export const QuotedAddOnSchema = z
    .strictObject({
    code: CatalogCodeSchema.describe('The add-on, by code — "website_extra_page".'),
    qty: z.int().min(1).max(100).default(1).describe('How many of it.'),
})
    .describe('One add-on on a project quote.');
/**
 * The composer body. `POST /api/clients/me/requests`, and `work_orders_draft`.
 *
 * One shape for both forms on the two pages that raise work: the free-text composer
 * on `requests.html` and the ticked rate-card items on `billing.html` §6. They differ
 * only in `source`, `catalogue_codes` and `preferred_start_on`.
 */
export const DraftWorkOrderSchema = z
    .strictObject({
    kind: WorkOrderKindSchema.default('extra').describe('extra by default, which is what a client raises. Staff and agents may draft a ' +
        'project, which gets a WO- reference and milestones.'),
    service: CatalogCodeSchema.describe('The service this is work on, by rate-card code — "social_content_base", ' +
        '"graphic_design", "website".'),
    title: z.string().min(3).max(200).describe('One line, as it will read in the ledger.'),
    scope: z
        .string()
        .max(10_000)
        .default('')
        .describe('What is wanted, in words. The composer’s "Describe the work".'),
    timing: WorkOrderTimingSchema.default('this_month'),
    source: RequestSourceSchema.default('composer'),
    catalogue_codes: z
        .array(CatalogCodeSchema)
        .max(50)
        .default([])
        .describe('The rate-card codes ticked on the billing page. Empty for a free-text request.'),
    preferred_start_on: DateOnlySchema.optional().describe('The billing day the client would like it to start on. Left out for "whenever you can".'),
    parent_ref: AnyWorkOrderRefSchema.optional().describe('The project this is a scope change on. Use work_orders_add_scope_change instead of ' +
        'setting this by hand.'),
})
    .describe('A new request: what is wanted, for which service, and how soon.');
export const UpdateWorkOrderSchema = z
    .strictObject({
    title: z
        .string()
        .min(3)
        .max(200)
        .optional()
        .describe('A new one-line title. Left out, it keeps the one it has.'),
    scope: z
        .string()
        .max(10_000)
        .optional()
        .describe('A new description of what is wanted. Left out, unchanged.'),
    service: CatalogCodeSchema.optional().describe('A different rate-card service, by code. Left out, unchanged.'),
    timing: WorkOrderTimingSchema.optional().describe('How soon it is wanted, changed.'),
})
    .describe('Change a draft, or take a proposed quote back to draft so it can be re-priced. Never ' +
    'a silent edit: a quote the client is looking at goes back to draft first.');
export const PriceWorkOrderSchema = z
    .strictObject({
    template: CatalogCodeSchema.optional().describe('The project template to quote from, by code — "website_m", "branding_m". Leave it ' +
        'out for an extra, which is priced from units instead.'),
    add_ons: z
        .array(QuotedAddOnSchema)
        .max(50)
        .default([])
        .describe('The add-ons on top of the template, in the order they should read.'),
    units: z
        .array(PricedUnitSchema)
        .max(50)
        .default([])
        .describe('The rate-card units this extra is made of. One line each; the unit price is ' +
        'rounded to $5 at this client’s tier and then multiplied.'),
})
    .describe('What to price this work order as: a template with add-ons, or a list of rate-card ' +
    'units. Exactly one of the two.');
export const MilestoneScheduleSchema = z
    .strictObject({
    title: z.string().min(3).max(200).describe('What the milestone is called.'),
    percent: z
        .int()
        .min(1)
        .max(100)
        .describe('Its share of the total, as a whole percentage. They sum to 100.'),
    due_on: DateOnlySchema.optional().describe('When it is due, YYYY-MM-DD.'),
})
    .describe('One milestone of a proposed schedule.');
export const ProposeWorkOrderSchema = z
    .strictObject({
    milestones: z
        .array(MilestoneScheduleSchema)
        .max(12)
        .default([])
        .describe('The schedule for a project. Left out, 50% on approval and 50% on delivery ' +
        '(SERVICES.md). Ignored for an extra billed on a retainer.'),
    deposit_on_approval: z
        .boolean()
        .default(true)
        .describe('True to issue the first milestone as a deposit invoice the moment the client ' +
        'approves. False when the first milestone invoices on delivery like the rest.'),
    recurrence: WorkOrderRecurrenceSchema.optional().describe('monthly for an extra that repeats every period until it is cancelled.'),
})
    .describe('Send the priced quote to the client. It holds for thirty days.');
//# sourceMappingURL=work-orders.js.map